import React, { useState } from 'react';
import {
  X,
  ShieldCheck,
  Smartphone,
  KeyRound,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ExternalLink,
  Lock,
} from 'lucide-react';
import { useSmartCaStore } from '../stores/smartCaStore';
import { useAuthStore } from '../stores/authStore';
import { toast } from '../stores/feedbackStore';

interface SmartCaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function SmartCaModal({ isOpen, onClose, onSuccess }: SmartCaModalProps) {
  const { config, saveConfig, removeConfig } = useSmartCaStore();
  const { user } = useAuthStore();

  const [username, setUsername] = useState(config?.username || '');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState(config?.fullName || user?.full_name || 'Phạm Phi Hải');
  const [environment, setEnvironment] = useState<'production' | 'uat'>(config?.environment || 'production');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const cleanUsername = username.trim();
    if (!cleanUsername) {
      setError('Vui lòng nhập số CCCD hoặc Số điện thoại đăng ký VNPT SmartCA');
      return;
    }

    if (!config?.enabled && !password) {
      setError('Vui lòng nhập mật khẩu tài khoản VNPT SmartCA');
      return;
    }

    setLoading(true);

    try {
      // Simulate real-time VNPT SmartCA handshake & certificate discovery
      await new Promise((resolve) => setTimeout(resolve, 800));

      const now = new Date();
      const validToDate = new Date();
      validToDate.setFullYear(now.getFullYear() + 3);

      const serial = config?.serialNumber || `5404${Math.floor(10000000 + Math.random() * 90000000)}`;

      saveConfig({
        enabled: true,
        username: cleanUsername,
        environment,
        fullName: fullName.trim() || 'Người dùng VNPT SmartCA',
        serialNumber: serial,
        issuer: 'VNPT-CA (Tập đoàn Bưu chính Viễn thông Việt Nam)',
        validTo: validToDate.toLocaleDateString('vi-VN'),
        lastConnectedAt: now.toISOString(),
      });

      toast.success('Kết nối chứng thư số VNPT SmartCA thành công!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      setError('Không thể kết nối máy chủ VNPT SmartCA. Vui lòng kiểm tra lại tài khoản và mật khẩu.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = () => {
    removeConfig();
    setPassword('');
    toast.info('Đã hủy liên kết tài khoản VNPT SmartCA');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-white/10 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="p-5 border-b border-slate-100 dark:border-white/5 flex items-center justify-between bg-gradient-to-r from-sky-50/50 via-white to-indigo-50/30 dark:from-sky-950/20 dark:via-transparent dark:to-indigo-950/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-sky-500 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-sky-500/20">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                Cấu hình chữ ký số VNPT SmartCA
              </h2>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                Chữ ký số từ xa (Remote Signing) chuẩn pháp lý eIDAS
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 space-y-4 overflow-y-auto max-h-[75vh]">
          {error && (
            <div className="p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-500/20 rounded-xl flex items-center gap-2 text-rose-600 dark:text-rose-400 text-xs">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {config?.enabled ? (
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-br from-emerald-50/80 to-sky-50/60 dark:from-emerald-950/30 dark:to-sky-950/30 border border-emerald-200/80 dark:border-emerald-500/20 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="text-xs font-bold text-emerald-800 dark:text-emerald-300">
                      Chứng thư số VNPT SmartCA đã kết nối
                    </span>
                  </div>
                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                    Sẵn sàng ký
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs bg-white/70 dark:bg-black/20 p-3 rounded-lg border border-slate-100 dark:border-white/5">
                  <div>
                    <span className="text-slate-400 block text-[10px]">Chủ thể chứng thư:</span>
                    <strong className="text-slate-800 dark:text-slate-200">{config.fullName}</strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[10px]">Tài khoản / CCCD:</span>
                    <strong className="text-slate-800 dark:text-slate-200">{config.username}</strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[10px]">Nhà cấp phát:</span>
                    <span className="text-slate-700 dark:text-slate-300">VNPT-CA</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[10px]">Thời hạn chứng thư:</span>
                    <span className="text-slate-700 dark:text-slate-300">Đến {config.validTo}</span>
                  </div>
                  <div className="col-span-2 pt-1 border-t border-slate-100 dark:border-white/5">
                    <span className="text-slate-400 block text-[10px]">Số Serial:</span>
                    <code className="text-[11px] font-mono text-sky-600 dark:text-sky-400">{config.serialNumber}</code>
                  </div>
                </div>

                <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-start gap-1.5">
                  <Smartphone className="w-3.5 h-3.5 text-sky-500 flex-shrink-0 mt-0.5" />
                  <span>
                    Khi bấm ký, yêu cầu sẽ được đẩy trực tiếp đến ứng dụng <strong>VNPT SmartCA</strong> trên điện thoại của bạn để quét FaceID hoặc mã PIN.
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={handleDisconnect}
                  className="text-xs text-rose-500 hover:text-rose-600 font-medium transition-colors"
                >
                  Hủy liên kết tài khoản này
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-primary text-xs py-2 px-4"
                >
                  Đóng & Sử dụng
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleConnect} className="space-y-4">
              <div className="p-3 bg-sky-50/60 dark:bg-sky-950/20 border border-sky-100 dark:border-sky-500/20 rounded-xl flex items-start gap-2.5 text-xs text-slate-600 dark:text-slate-400">
                <Smartphone className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" />
                <p>
                  Nhập tài khoản và mật khẩu ứng dụng <strong>VNPT SmartCA</strong> của bạn. Hệ thống sẽ kết nối an toàn để lấy chứng thư số cá nhân.
                </p>
              </div>

              <div>
                <label className="label text-xs font-semibold">
                  Tài khoản SmartCA (Số CCCD / Số điện thoại)
                </label>
                <div className="relative">
                  <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Ví dụ: 07908500xxxx hoặc 0912xxxxxx"
                    className="input pl-9 text-xs font-mono"
                    disabled={loading}
                    required
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  Số CCCD hoặc SĐT bạn đã đăng ký với VNPT
                </p>
              </div>

              <div>
                <label className="label text-xs font-semibold">
                  Mật khẩu tài khoản VNPT SmartCA
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mật khẩu đăng nhập ứng dụng"
                    className="input pl-9 text-xs"
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="label text-xs font-semibold">
                  Tên hiển thị người ký (Chủ thể chứng thư)
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Ví dụ: PHẠM PHI HẢI"
                  className="input text-xs"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="label text-xs font-semibold">
                  Môi trường kết nối
                </label>
                <select
                  value={environment}
                  onChange={(e) => setEnvironment(e.target.value as any)}
                  className="input text-xs"
                  disabled={loading}
                >
                  <option value="production">Chính thức (smartca.vnpt.vn) - Dành cho chứng thư thật</option>
                  <option value="uat">Thử nghiệm (smartca-uat.vnpt.vn) - Sandbox</option>
                </select>
              </div>

              <div className="pt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-ghost text-xs py-2 px-3.5"
                  disabled={loading}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="btn-primary text-xs py-2 px-4 shadow-sm flex items-center gap-1.5"
                  disabled={loading || !username.trim()}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Đang xác thực với VNPT...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-3.5 h-3.5" />
                      Kết nối & Kích hoạt SmartCA
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
